import axios from "axios";
import { API_BASE_URL } from "../utils/constants";
import authService from "./authService";

const isValidGuid = (str) => {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(str);
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60000, // 60 seconds (increased for large competitor analysis)
});

// Request interceptor - Add JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // Handle timeout errors - don't logout on timeout
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      console.error("Request timeout - server is taking too long to respond");
      return Promise.reject(
        new Error(
          "Request timeout. The server is taking too long to respond. Please try again."
        )
      );
    }

    // Handle network errors (backend not running)
    if (!error.response) {
      console.error("Network error: Backend is not running");
      authService.logout();
      return Promise.reject(
        new Error("Cannot connect to server. Please try again later.")
      );
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      authService.logout();
      return Promise.reject(new Error("Session expired. Please login again."));
    }

    // Extract error message from response
    // Backend returns { error: "message" } or { error: "message", details: "..." }
    const errorData = error.response?.data;
    let errorMessage = "An error occurred";

    if (errorData) {
      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        const header = errorData.error || errorData.message || "Erreur de validation";
        errorMessage = `${header} : ${errorData.errors.join(" | ")}`;
      } else if (errorData.error) {
        errorMessage = errorData.error;
        if (errorData.details) {
          errorMessage += `: ${errorData.details}`;
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    }

    console.error(
      "API Error:",
      errorMessage,
      "Status:",
      error.response?.status
    );
    const customError = new Error(errorMessage);
    customError.status = error.response?.status;
    return Promise.reject(customError);
  }
);

// API methods
export const studiesAPI = {
  /**
   * Get studies with pagination
   * @param {Object} options - { skip: 0, take: 20, search: '' }
   * @returns {Promise<{ items: Array, totalCount: number, hasMore: boolean }>}
   */
  list: async (options = {}) => {
    const { skip = 0, take = 20, search = '' } = options;
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    return apiClient.get(`/studies?skip=${skip}&take=${take}${searchParam}`);
  },

  /**
   * Get a specific study by ID
   */
  get: async (studyId) => {
    return apiClient.get(`/studies/${studyId}`);
  },

  /**
   * Create a new study
   */
  create: async (data) => {
    return apiClient.post("/studies", data);
  },

  /**
   * Get supported countries and languages for study creation
   * @returns {Promise<{ countries: Array, languages: Array }>}
   */
  getOptions: async () => {
    return apiClient.get("/study-options");
  },

  /**
   * Update the display name of a study (empty string or null resets to default).
   */
  updateName: async (studyId, name) => {
    return apiClient.patch(`/studies/${studyId}/name`, { name });
  },

  /**
   * Get study results (KPIs, competitors, etc.)
   */
  getResults: async (studyId) => {
    return apiClient.get(`/studies/${studyId}/results`);
  },

  /**
   * Get keyword similarities for a study
   */
  getSimilarities: async (studyId) => {
    return apiClient.get(`/studies/${studyId}/similarities`);
  },

  /**
   * Get SERP results for a study (from blob storage)
   */
  getSerpResults: async (studyId) => {
    return apiClient.get(`/studies/${studyId}/serp-results`);
  },

  /**
   * Get historical metrics for keywords in a study
   */
  getHistoricalMetrics: async (studyId) => {
    return apiClient.get(`/studies/${studyId}/keywords/historical`);
  },

  /**
   * Get categories for a study
   */
  getCategories: async (studyId) => {
    return apiClient.get(`/studies/${studyId}/categories`);
  },

  /**
   * Get filtered report by categories
   */
  getFilteredReport: async (studyId, categories, locations = []) => {
    return apiClient.post(`/studies/${studyId}/filtered-report`, { categories, locations });
  },

  /**
   * Retry a failed study
   */
  retry: async (studyId) => {
    console.log("[API] Retrying study:", studyId);
    console.log("[API] URL:", `${API_BASE_URL}/studies/${studyId}/retry`);
    return apiClient.post(`/studies/${studyId}/retry`);
  },

  /**
   * Cancel an in-progress study
   */
  cancel: async (studyId) => {
    return apiClient.post(`/studies/${studyId}/cancel`);
  },

  /**
   * Retry Google Ads phase for a specific study
   */
  retryGoogleAds: async (studyId) => {
    console.log("[API] Retrying Google Ads for study:", studyId);
    return apiClient.post(`/studies/${studyId}/retry-google-ads`);
  },

  /**
   * Retry Google Ads phase for ALL stuck studies (Admin only)
   */
  retryAllGoogleAds: async () => {
    console.log("[API] Retrying Google Ads for all stuck studies");
    return apiClient.post("/studies/retry-all-google-ads");
  },

  saveClusters: async (studyId, clustersData) => {
    if (!isValidGuid(studyId)) {
      throw new Error('Invalid study ID format');
    }
    return apiClient.post(`/studies/${studyId}/clusters`, { clustersData });
  },

  getClusters: async (studyId) => {
    if (!isValidGuid(studyId)) {
      throw new Error('Invalid study ID format');
    }
    return apiClient.get(`/studies/${studyId}/clusters`);
  },

  /**
   * Relaunch a completed or failed study (creates new snapshot)
   */
  relaunch: async (studyId, name = null) => {
    const data = name ? { name } : {};
    return apiClient.post(`/studies/${studyId}/relaunch`, data);
  },

  /**
   * Get all snapshots for a study (evolution history)
   */
  getSnapshots: async (studyId) => {
    return apiClient.get(`/studies/${studyId}/snapshots`);
  },

  /**
   * Compare two studies
   */
  compare: async (studyIdA, studyIdB) => {
    return apiClient.get(`/studies/${studyIdA}/compare?with=${studyIdB}`);
  },

  /**
   * Get schedule for a study
   */
  getSchedule: async (studyId) => {
    return apiClient.get(`/studies/${studyId}/schedule`);
  },

  /**
   * Create or update schedule for a study
   */
  saveSchedule: async (studyId, scheduleData) => {
    return apiClient.post(`/studies/${studyId}/schedule`, scheduleData);
  },

  /**
   * Delete schedule for a study
   */
  deleteSchedule: async (studyId) => {
    return apiClient.delete(`/studies/${studyId}/schedule`);
  },
};

// Dashboard stats API (mock for now, replace with real endpoint)
export const dashboardAPI = {
  getStats: async () => {
    // Mock data - replace with real API call
    return {
      activeProjects: { value: 12, evolution: 20 },
      analyzedDomains: { value: 247, evolution: 15 },
      trackedKeywords: { value: 3456, evolution: 8 },
      totalTraffic: { value: 125000, evolution: -12 },
    };
  },
};

// Convenience method for historical metrics
export const getKeywordHistoricalMetrics = (studyId) => {
  return studiesAPI.getHistoricalMetrics(studyId);
};

// Folders API
export const foldersAPI = {
  list: async () => {
    return apiClient.get("/folders");
  },

  getStudies: async (folderId) => {
    return apiClient.get(`/folders/${folderId}/studies`);
  },

  create: async (data) => {
    return apiClient.post("/folders", data);
  },

  update: async (folderId, data) => {
    return apiClient.put(`/folders/${folderId}`, data);
  },

  delete: async (folderId) => {
    return apiClient.delete(`/folders/${folderId}`);
  },

  assignStudy: async (folderId, studyId) => {
    return apiClient.put(`/folders/${folderId}/studies/${studyId}`);
  },

  removeStudy: async (studyId) => {
    return apiClient.delete(`/folders/studies/${studyId}`);
  },
};

// User Management APIs
export const userAPI = {
  list: async () => {
    return apiClient.get("/users/list");
  },

  create: async (userData) => {
    return apiClient.post("/users/create", userData);
  },

  update: async (userId, userData) => {
    return apiClient.put(`/users/${userId}`, userData);
  },

  delete: async (userId) => {
    return apiClient.delete(`/users/${userId}`);
  },
};

export const locationsAPI = {
  geocode: async (expectedCountryCode, inputs) => {
    return apiClient.post('/locations/geocode', { expectedCountryCode, inputs });
  },
};

export default apiClient;
